"use client";

import { Elements } from "@stripe/react-stripe-js";
import type { Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import getStripe from "@repo/stripe/context/utils";
import ExpressCheckoutButton from "@repo/stripe/express-checkout";
import * as Sentry from "@sentry/nextjs";
import { createSetup } from "@repo/stripe/actions/setup";
import { fetchPricingPlanByKey } from "@repo/stripe/actions";
import { getStripeAttributionMetadata } from "@repo/statsig";
import { BRAND } from "@repo/tailwind-config/brand";
import { usePricingPlanKeys } from "src/lib/pricing-plan-keys";
import { useFreeTrialExperiment } from "src/lib/use-free-trial-experiment";
import { Routes } from "src/lib/routes";
import { useInitialContext } from "@repo/sdk";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { KinsaInitialContext } from "src/lib/definitions";
import { useTrustedUserEmail } from "src/lib/owlet-trusted-email";
import type { ProductType, BillingPeriod } from "../hooks/use-paywall-state";

interface PlanInfo {
  id: string;
  price: number; // cents
  interval: "month" | "year";
  trialDays: number;
}

interface PaywallExpressCheckoutProps {
  selectedProduct: ProductType;
  billingPeriod: BillingPeriod;
  setIsProcessing: (v: boolean) => void;
  /** Called when we know whether express checkout buttons are available */
  onAvailabilityChange?: (available: boolean) => void;
}

const ELEMENTS_OPTIONS: StripeElementsOptions = {
  mode: "setup",
  currency: "usd",
  setup_future_usage: "off_session",
};

/**
 * Error boundary to prevent Stripe initialization failures from crashing the page.
 */
class ExpressCheckoutErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.warn("Express checkout failed to load:", error.message);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

/**
 * Self-contained Apple Pay / Google Pay button for the paywall footer.
 *
 * Only mounts the Stripe Elements provider once both Stripe and plan data are
 * ready. This avoids injecting invisible Stripe iframes that block page clicks.
 */
export function PaywallExpressCheckout(props: PaywallExpressCheckoutProps) {
  return (
    <ExpressCheckoutErrorBoundary>
      <ExpressCheckoutLoader {...props} />
    </ExpressCheckoutErrorBoundary>
  );
}

/**
 * Loads Stripe + plan data BEFORE mounting Elements, so no invisible iframes
 * are injected until we're actually ready to show the button.
 */
function ExpressCheckoutLoader({
  selectedProduct,
  billingPeriod,
  setIsProcessing,
  onAvailabilityChange,
}: PaywallExpressCheckoutProps) {
  const pricingPlanKeys = usePricingPlanKeys();
  const { trialDays } = useFreeTrialExperiment();
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [plan, setPlan] = useState<PlanInfo | null>(null);

  // Resolve Stripe instance asynchronously.
  // getStripe() returns a cached singleton promise — don't use a cancelled flag,
  // as remounting would block the already-resolved promise from setting state.
  useEffect(() => {
    getStripe()
      .then((s) => {
        if (s) setStripe(s);
      })
      .catch(() => {
        Sentry.captureMessage(
          "Stripe failed to load — likely blocked by ad-blocker or privacy extension",
          { level: "warning" }
        );
      });
  }, []);

  // Fetch the selected plan's Stripe pricing
  const lookupKey =
    pricingPlanKeys[selectedProduct]?.[billingPeriod] ??
    pricingPlanKeys.unlimitedRx[billingPeriod];

  useEffect(() => {
    let cancelled = false;

    fetchPricingPlanByKey(lookupKey)
      .then((data) => {
        if (cancelled) return;
        if (data.id && data.unit_amount) {
          setPlan({
            id: data.id,
            price: data.unit_amount,
            interval: billingPeriod === "yearly" ? "year" : "month",
            trialDays: data.trial_days ?? trialDays,
          });
        }
      })
      .catch(() => {
        // Pricing fetch failed — silently skip express checkout
      });

    return () => {
      cancelled = true;
    };
  }, [lookupKey, billingPeriod, trialDays]);

  // Reserve the button's vertical space with a skeleton while Stripe + plan
  // load. Height matches `buttonHeight: 48` in ExpressCheckoutButton so the
  // footer doesn't shift when the wallet button appears (PE-1291).
  if (!stripe || !plan) {
    return (
      <div className="express-checkout-container">
        <div
          aria-hidden
          className="h-12 w-full rounded-md bg-gradient-to-r from-white/10 via-white/20 to-white/10 bg-[length:200%_100%] animate-shimmer"
        />
      </div>
    );
  }

  // Own Elements provider required — the paywall doesn't have a Stripe Elements context,
  // and the checkout page's Elements uses paymentMethodTypes: ["card"] which rejects wallet payments
  return (
    <Elements stripe={stripe} options={ELEMENTS_OPTIONS}>
      <div className="w-full [&_.express-checkout-container]:w-full relative min-h-12">
        {/* Underlay shows through during the brief unmount → mount on plan
            change (ExpressCheckoutElement bakes the wallet sheet amount at
            mount, so we remount on price change — see key below). PE-1291. */}
        <div
          aria-hidden
          className="absolute inset-0 h-12 w-full rounded-md bg-gradient-to-r from-white/10 via-white/20 to-white/10 bg-[length:200%_100%] animate-shimmer pointer-events-none"
        />
        <ExpressCheckoutReady
          plan={plan}
          selectedProduct={selectedProduct}
          billingPeriod={billingPeriod}
          setIsProcessing={setIsProcessing}
          onAvailabilityChange={onAvailabilityChange}
        />
      </div>
    </Elements>
  );
}

function ExpressCheckoutReady({
  plan,
  selectedProduct,
  billingPeriod,
  setIsProcessing,
  onAvailabilityChange,
}: {
  plan: PlanInfo;
  selectedProduct: ProductType;
  billingPeriod: BillingPeriod;
  setIsProcessing: (v: boolean) => void;
  onAvailabilityChange?: (available: boolean) => void;
}) {
  const context = useInitialContext<KinsaInitialContext>();
  const account = context?.account;
  const selectedPatient =
    account?.selectedPatient || account?.coveredPersons?.[0];
  const fullName = selectedPatient
    ? `${selectedPatient.firstName} ${selectedPatient.lastName}`
    : "";

  // OwletAccount has no `email` field at runtime — only Kinsa's bridge
  // populates `account.email`. For Owlet we fall back to the bridge's
  // id_token JWT (`user_email` claim) so we don't pass an empty email
  // into Stripe's customer search and trip the empty-email-list trap.
  const trustedEmail = useTrustedUserEmail();

  // One-shot diagnostic: confirm what the native bridge actually injects
  // on `account` at runtime. Static types claim OwletAccount has no
  // `email`, but the bridge is owned by the native team — this breadcrumb
  // captures the real key set in prod so we can verify or disprove.
  // Logs keys only (no values) to avoid PII leakage.
  useEffect(() => {
    if (!account) return;
    Sentry.addBreadcrumb({
      category: "paywall.bridge",
      message: "initialContext.account keys",
      level: "info",
      data: {
        accountKeys: Object.keys(account),
        customKeys: account.custom ? Object.keys(account.custom) : [],
        hasEmailField: "email" in account,
        hasTrustedEmail: !!trustedEmail,
      },
    });
    // Run once per mount; trustedEmail is included in the snapshot but the
    // breadcrumb itself doesn't need to refire if it changes later.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const userInfo = useMemo(
    () => ({
      email: account?.email || trustedEmail || "",
      name: fullName,
      metadata: {
        externalAccountId: account?.id || "",
        selectedPlan: selectedProduct,
        cadence: billingPeriod,
      },
    }),
    [account, fullName, selectedProduct, billingPeriod, trustedEmail]
  );

  const productName =
    selectedProduct === "unlimitedRx"
      ? "Fast Prescriptions"
      : "Comprehensive Kids Care";

  const handleCreateSetup = useCallback(
    async (info: {
      email: string;
      name: string;
      metadata: Record<string, string>;
    }) => {
      // Use createSetup which creates a setup intent with
      // automatic_payment_methods (required for Apple Pay / Google Pay).
      // The setup_intent.succeeded webhook creates the subscription.
      // Attribution metadata (journey_id, brand, trial_days) is merged so
      // the webhook can stamp it on every server-side event for end-to-end
      // funnel attribution. paywall_variant prop-threading is a follow-up;
      // until then the Kinsa two-prod paywall passes its own constant via
      // a future prop.
      return createSetup({
        email: info.email,
        name: info.name,
        metadata: {
          ...userInfo.metadata,
          ...info.metadata,
          ...getStripeAttributionMetadata({
            brand: BRAND,
            trialDays: plan.trialDays,
          }),
        },
        planId: plan.id,
        planPrice: plan.price,
        planInterval: plan.interval,
        trialDays: plan.trialDays,
        productName,
      });
    },
    [plan, userInfo, productName]
  );

  // Remount on price change — ExpressCheckoutElement bakes the wallet sheet
  // amount at mount and doesn't pick up options updates, so without this key
  // the Apple Pay sheet would show the previous billing period's price.
  // The skeleton underlay in ExpressCheckoutLoader hides the visual gap.
  const elementKey = `${plan.id}-${plan.interval}-${plan.price}`;

  return (
    <ExpressCheckoutButton
      key={elementKey}
      successPath={Routes.HOME}
      errorPath={Routes.ERROR}
      setIsProcessingPayment={setIsProcessing}
      planDetails={plan}
      productName={productName}
      source="paywall"
      onAvailabilityChange={onAvailabilityChange}
      userInfo={userInfo}
      onCreateSetup={handleCreateSetup}
    />
  );
}
